/**
 * Court Piece bot.
 *
 * - Trump: the suit that is longest and strongest among the five cards the caller sees first.
 * - Play: partnership-aware heuristic. It takes tricks as cheaply as possible, lets a winning partner keep the
 *   trick, ruffs when void, and leads master cards. NORMAL and above track played cards to recognise masters.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  courtPieceBestSuit,
  courtPiecePartnerOf,
  courtPieceTrickWinner,
  type CourtPieceAction,
  type CourtPieceLegal,
  type CourtPieceTrickPlay,
  type CourtPieceView,
} from './engine.js';

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: CourtPieceView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => !seen.has(c))) };
}

function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) {
    const x = parseCard(u);
    if (x.suit === c.suit && x.rank > c.rank) return false;
  }
  return true;
}

function wouldWin(trick: readonly CourtPieceTrickPlay[], seat: number, card: CardId, trump: Suit): boolean {
  if (trick.length === 0) return true;
  return courtPieceTrickWinner([...trick, { seat, card }], trump) === seat;
}

function chooseCard(view: CourtPieceView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat!;
  const partner = courtPiecePartnerOf(me);
  const trump = view.trump!;
  const trick = view.trick;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  const power = (id: CardId) => {
    const c = parseCard(id);
    return (c.suit === trump ? 100 : 0) + c.rank;
  };
  const lowest = (cards: CardId[]) => [...cards].sort((a, b) => power(a) - power(b))[0]!;
  const highest = (cards: CardId[]) => [...cards].sort((a, b) => power(b) - power(a))[0]!;
  const suitCounts = (cards: CardId[]) => {
    const counts = new Map<Suit, number>();
    cards.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
    return counts;
  };
  const dump = (cards: CardId[]) => {
    const side = cards.filter((c) => parseCard(c).suit !== trump);
    if (side.length === 0) return lowest(cards);
    // Shed from the suit held longest, lowest first — keeps short suits for ruffing.
    const longest = [...suitCounts(side).entries()].sort((a, b) => b[1] - a[1])[0]![0];
    return lowest(side.filter((c) => parseCard(c).suit === longest));
  };

  if (trick.length === 0) {
    // Leading: a master side-suit card, then master trumps from a long trump suit, else a low card from the shortest side suit.
    const side = legal.filter((c) => parseCard(c).suit !== trump);
    const masters = k ? side.filter((c) => isMaster(c, k)) : side.filter((c) => parseCard(c).rank === 14);
    if (masters.length) return highest(masters);
    const trumps = legal.filter((c) => parseCard(c).suit === trump);
    if (k && trumps.length >= 4) {
      const top = trumps.filter((c) => isMaster(c, k));
      if (top.length) return highest(top);
    }
    if (side.length) {
      const shortSuit = [...suitCounts(side).entries()].sort((a, b) => a[1] - b[1])[0]![0];
      return lowest(side.filter((c) => parseCard(c).suit === shortSuit));
    }
    return lowest(legal);
  }

  const winnerSeat = courtPieceTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 3;
  if (winnerSeat === partner) {
    // Partner is winning: keep our winners unless a later opponent could still overtake a non-master card.
    if (last || !k || isMaster(winnerCard, k)) return dump(legal);
    const over = legal.filter((c) => wouldWin(trick, me, c, trump) && parseCard(c).suit === parseCard(winnerCard).suit);
    if (over.length && parseCard(winnerCard).rank < 10) return lowest(over);
    return dump(legal);
  }
  const winners = legal.filter((c) => wouldWin(trick, me, c, trump));
  if (winners.length === 0) return dump(legal);
  if (last) return lowest(winners);
  const safe = k ? winners.filter((c) => isMaster(c, k) || parseCard(c).suit === trump) : winners;
  return lowest(safe.length ? safe : winners);
}

export const courtPieceBot: BotEngine<CourtPieceView, CourtPieceLegal, CourtPieceAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'trump') return { type: 'trump', suit: courtPieceBestSuit(view.hand) };
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / (2 * view.config.targetScore)));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'trump') return { type: 'trump', suit: courtPieceBestSuit(view.hand) };
    if (legal.kind === 'play') {
      const trump = view.trump;
      const power = (id: CardId) => (parseCard(id).suit === trump ? 100 : 0) + parseCard(id).rank;
      return { type: 'play', card: [...legal.cards].sort((a, b) => power(a) - power(b))[0]! };
    }
    throw new Error('No legal action');
  },
};
